import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { objectToCamel } from 'ts-case-convert';
import { Errors } from '@blink/components/src/constants/errors';
import { parseUnits } from 'ethers';

import { setPrices, setPricesLoading } from '@/shared/store/borrows/actions';
import { assetPriceGeneralErrorAction } from '@/shared/store/errors/actions';
import { Store } from '@/shared/store';
import {
    Agreement,
    getPrice,
    getPriceByProvider,
    StressTestMatrix,
    Token,
} from '@/shared/api/borrow';
import { useCentrifugeContext } from '@/app/providers';

type UseGetPricesProps = {
    assetsToCollateral: Token[];
    agreement: Agreement;
    agreementAddress: string;
    assetSymbol: string;
};

type EstimateRiskFactorResponse = {
    risk_factor: string;
    market_value: string;
    stress_tested_value: string;
};

const withRetries = async <T>(
    fn: () => Promise<T>,
    options?: {
        retries?: number;
        delayMs?: number;
        fallback?: () => Promise<T>;
    },
): Promise<T | null> => {
    const { retries = 3, delayMs = 1000, fallback } = options || {};

    for (let attempt = 0; attempt < retries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            console.warn(`Retry attempt ${attempt + 1} failed`, error);
            if (attempt < retries - 1) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }
    }

    if (fallback) {
        console.warn('All retries failed. Trying fallback...');
        try {
            return await fallback();
        } catch (fallbackError) {
            console.error('Fallback also failed', fallbackError);
            return null;
        }
    }

    console.error(`All ${retries} attempts failed`);
    return null;
};

export const useGetPrices = ({
    assetsToCollateral,
    agreementAddress,
    agreement,
    assetSymbol,
}: UseGetPricesProps) => {
    const { prices, loading } = useSelector((state: Store) => ({
        prices: state.borrows.userConversionRates,
        loading: state.borrows.userBorrowsClosedLoading,
    }));

    const dispatch = useDispatch();
    const { centrifuge } = useCentrifugeContext();

    const getPriceForAsset = async (asset: Token): Promise<(Token & { price: string }) | null> => {
        return withRetries(async () => {
            const leverageAmount = parseUnits('1', agreement.leverage.decimals).toString();
            const collateralAmount = parseUnits('1', asset.decimals).toString();

            const params = {
                leverage: {
                    address: agreement.leverage.address,
                    name: agreement.leverage.name,
                    symbol: assetSymbol,
                    decimals: agreement.leverage.decimals,
                    amount: leverageAmount,
                },
                collateral: [
                    {
                        name: asset.name,
                        amount: collateralAmount,
                        address: asset.address,
                        symbol: asset.symbol,
                        decimals: asset.decimals,
                    },
                ],
                agreement_address: agreementAddress,
            };

            const response = await centrifuge?.rpc('estimate_risk_factor', params);
            const parsedData =
                typeof response?.data === 'string' ? JSON.parse(response.data) : response?.data;

            if (parsedData?.error) {
                console.error(parsedData.error);
                throw new Error(parsedData.error);
            }

            const data = objectToCamel(parsedData as { result: EstimateRiskFactorResponse });
            const price = data?.result?.marketValue;
            const priceWithoutLeverage = Number(price) - 1;

            return {
                ...asset,
                price: priceWithoutLeverage.toFixed(agreement.leverage.decimals),
            };
        });
    };

    const getPriceByProviderRequest = (item: Token, collateralData: StressTestMatrix) => {
        return withRetries(
            async () => {
                const result = await getPriceByProvider(
                    item.address!,
                    agreement.leverage.address!,
                    collateralData.priceProvider,
                    collateralData.priceModel,
                );

                if (!result?.price) {
                    console.warn(`Missing price in provider response for ${item.symbol}`);
                    return null;
                }

                return {
                    ...item,
                    price: result.price,
                };
            },
            { fallback: () => getPriceForAsset(item), retries: 0 },
        );
    };

    const getBasePriceRequest = (item: Token) => {
        return withRetries(
            async () => {
                const result = await getPrice(item.address!, agreement.leverage.address!);

                if (!result?.price) {
                    console.warn(`Missing price in response for ${item.symbol}`);
                    return null;
                }

                return {
                    ...item,
                    price: result.price,
                };
            },
            { fallback: () => getPriceForAsset(item), retries: 0 },
        );
    };

    const fetchPrices = useCallback(async () => {
        if (!assetsToCollateral?.length || !agreementAddress || !agreement) return;

        dispatch(setPricesLoading(true));

        try {
            const pricePromises = assetsToCollateral.map(async (item) => {
                const collateralData = agreement.stressTestMatrix.find(
                    (a) => a.address === item.address,
                );

                if (item.address === agreement.leverage.address) {
                    return {
                        ...item,
                        price: '1',
                    };
                }

                if (
                    !collateralData?.priceProvider ||
                    !collateralData?.priceModel ||
                    collateralData?.priceProvider === 'N/A' ||
                    collateralData?.priceModel === 'N/A'
                ) {
                    console.warn(`Missing price provider or model for ${item.symbol}`);
                    return getBasePriceRequest(item);
                }

                return getPriceByProviderRequest(item, collateralData);
            });

            const results = await Promise.all(pricePromises);
            const validResults = results.filter(Boolean) as Array<Token & { price: string }>;

            if (!validResults.length) {
                dispatch(assetPriceGeneralErrorAction({ message: Errors.NO_PRICES_AVAILABLE }));
            }

            dispatch(setPrices(validResults));
        } catch (error) {
            console.error('Unexpected error while fetching prices', error);
            dispatch(assetPriceGeneralErrorAction({ message: Errors.NO_PRICES_AVAILABLE }));
        } finally {
            dispatch(setPricesLoading(false));
        }
    }, [assetsToCollateral, agreementAddress, agreement.stressTestMatrix]);

    useEffect(() => {
        fetchPrices();
    }, [fetchPrices]);

    return { prices, loading };
};
